import { memo } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Brain, LineChart, Database, Users, Globe, ArrowRight, Lightbulb } from "lucide-react";

const aboutFeatures = [
  {
    title: "Comprehensive Assessment",
    description: "Measure current AI capabilities and maturity level through a detailed evaluation of your organization's AI initiatives, infrastructure, and practices.",
    icon: Brain
  },
  {
    title: "Industry Benchmarking",
    description: "Compare your organization's AI maturity against industry-specific benchmarks to understand your competitive position and identify areas for improvement.",
    icon: LineChart
  },
  {
    title: "Strategic Recommendations",
    description: "Get actionable, tailored recommendations to enhance your AI capabilities and align them with your business objectives.",
    icon: Database
  },
  {
    title: "Progress Tracking",
    description: "Monitor your organization's AI maturity journey over time with systematic reassessments and detailed progress reports.",
    icon: Users
  },
  {
    title: "Global Best Practices",
    description: "Access insights derived from global AI implementation success stories and industry-leading practices.",
    icon: Globe
  },
  {
    title: "Continuous Learning & Improvement",
    description: "Leverage AI-driven insights to continuously adapt and enhance your organization's AI capabilities through iterative improvements.",
    icon: Lightbulb
  }
] as const;

const AboutSectionComponent = () => {
  const navigate = useNavigate();

  return (
    <section className="py-12 sm:py-20 bg-white">
      <div className="container mx-auto max-w-6xl px-4">
        <div className="space-y-8 sm:space-y-12">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f2d69] mb-4 sm:mb-6">About AIMAA</h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
              AIMAA (AI Maturity Assessment and Alignment) is a comprehensive framework designed to help organizations 
              measure and enhance their AI capabilities through systematic assessment and strategic alignment. Our 
              methodology combines industry expertise, data-driven insights, and proven best practices to guide 
              organizations in their AI transformation journey.
            </p>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Whether you're just starting with AI or looking to optimize your existing AI initiatives, AIMAA 
              provides the structured approach needed to evaluate, benchmark, and improve your organization's 
              AI maturity level.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {aboutFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:-translate-y-1 animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#0f2d69]/5 rounded-lg group-hover:bg-[#0f2d69]/10 transition-colors">
                    <feature.icon className="h-6 w-6 text-[#0f2d69]" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-[#0f2d69] mb-2">{feature.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center animate-fade-in">
            <Button 
              onClick={() => navigate("/assessment")}
              className="w-full sm:w-auto h-12 px-6 sm:px-8 text-base sm:text-lg bg-[#0f2d69] hover:bg-[#0f2d69]/90 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Start Your Assessment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const AboutSection = memo(AboutSectionComponent);
export default AboutSection;
